import { pointsToYen } from "@/utils/pointsToYen";
import { AvatarIcon } from "@roman/shared/components/Icons/AvatarIcon";
import type { EntryResult } from "@roman/shared/types/Result";
import { cn } from "@roman/shared/utils";
import { formatName } from "@roman/shared/utils/name";
import { isEqual } from "lodash";
import { useEffect, useState } from "react";

const BLINK_INTERVAL = 10000;
export const SelectEntryInfo = ({
  selectedEntry,
  totalEntries,
  totalPrizeOfUser,
}: {
  selectedEntry: EntryResult;
  totalEntries: number;
  totalPrizeOfUser: number;
}) => {
  const [stats, setStats] = useState<Record<string, number>>({});
  const [statsBlink, setStatsBlink] = useState<Record<string, boolean>>({
    rank: false,
    prize: false,
    totalPrizeOfUser: false,
  });

  useEffect(() => {
    const newStats: Record<string, number> = {
      rank: selectedEntry.rank,
      prize: Number.parseInt(selectedEntry.prize),
      totalPrizeOfUser,
    };

    if (isEqual(stats, newStats)) {
      return;
    }

    const blinkUpdates: Record<string, boolean> = {};
    let shouldBlink = false;

    for (const key of Object.keys(newStats)) {
      if (stats[key] !== newStats[key]) {
        blinkUpdates[key] = true;

        if (stats[key]) {
          shouldBlink = true;
        }
      }
    }

    if (shouldBlink) {
      setStatsBlink((prev) => ({ ...prev, ...blinkUpdates }));
      setTimeout(() => setStatsBlink({ rank: false, prize: false, totalPrizeOfUser: false }), BLINK_INTERVAL);
    }

    setStats(newStats);
  }, [selectedEntry, stats, totalPrizeOfUser]);

  return (
    <div className="mt-5">
      <div className="flex flex-col items-center justify-center space-y-[2px]">
        {selectedEntry.userPhotoUrl ? (
          <div className="w-[50px] h-[50px]">
            <img
              src={selectedEntry.userPhotoUrl || "/assets/player-shirt.png"}
              alt={selectedEntry.userName.substring(0, 1)}
              className="rounded-full"
              referrerPolicy="no-referrer"
            />
          </div>
        ) : (
          <div className="content-center justify-items-center rounded-full w-[50px] h-[50px] bg-secondary">
            <AvatarIcon />
          </div>
        )}
        <span className="font-semibold text-sm">{formatName(selectedEntry.userName)}</span>
      </div>

      <div className="grid grid-cols-3 gap-[1px] mt-4 py-1 w-full text-center">
        <div className="flex flex-col items-center justify-center w-full bg-primary">
          <div className="text-white text-xs leading-[12px] py-[9px]">総獲得賞金額</div>
          <div className="bg-[#004BC3] w-full text-[#4AFFFC] text-base leading-[16px] py-[7px] font-bold">
            <span className={cn(statsBlink.totalPrizeOfUser && "animate-blink")}>
              {pointsToYen(stats.totalPrizeOfUser || totalPrizeOfUser)}
              <span>円</span>
            </span>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center w-full bg-primary">
          <div className="text-white text-xs leading-[12px] py-[9px]">獲得賞金額</div>
          <div className="bg-[#004BC3] text-[#4AFFFC] w-full text-base leading-[16px] py-[7px] font-bold">
            <span className={cn(statsBlink.prize && "animate-blink")}>
              {pointsToYen(stats.prize || selectedEntry.prize)}
              <span>円</span>
            </span>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center w-full bg-primary">
          <div className="text-white text-xs leading-[12px] py-[9px]">順位</div>
          <div className="bg-[#004BC3] text-white w-full text-base leading-[16px] py-[7px] font-bold">
            <span className={cn("text-[#4AFFFC]", statsBlink.rank && "animate-blink")}>
              {stats.rank || selectedEntry.rank}
            </span>
            <span>/</span>
            <span>{totalEntries}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
